import { useNavigate } from 'react-router-dom';
import type { CreditEntry } from '../types';
import React from 'react';
import { EmptyLayer } from './LayerPlayer';

interface LayerCreditsProps {
  accentColor: string;
  credits: CreditEntry[];
  hasCreditsLayer: boolean;
  compact?: boolean;
}

const PATRON_COLOR = '#ffd700';

const COLLECTIVE_TYPES = [
  {
    value: 'patron' as const,
    label: 'Patron',
    color: PATRON_COLOR,
    icon: 'ri-vip-crown-line',
    description: '累計10,000pt以上の共鳴を果たした深淵の観測者。アーティストの活動を支える真のサポーター。',
  },
  {
    value: 'member' as const,
    label: 'Member',
    color: '#00ff41',
    icon: 'ri-shield-star-line',
    description: '恒常的なパートナー。共に「衝動」を形にし、活動のアイデンティティを根底から分かち合う存在。',
  },
  {
    value: 'support' as const,
    label: 'Support',
    color: '#00d9ff',
    icon: 'ri-flashlight-line',
    description: '一時的に重なり合う協力者。スキルや個性を持ち寄り、その瞬間の「響き」を拡張するプロフェッショナル。',
  },
];

// ── PATRON ランキング行コンポーネント ──────────────────────────────────
interface PatronRowProps {
  credit: CreditEntry;
  rank: number;
  compact: boolean;
  navigate: ReturnType<typeof useNavigate>;
}

function PatronRow({ credit, rank, compact, navigate }: PatronRowProps) {
  const rankColors: Record<number, { border: string; glow: string; badge: string }> = {
    1: { border: '#ffd700', glow: '#ffd70060', badge: 'bg-gradient-to-br from-yellow-400 to-amber-500' },
    2: { border: '#c0c0c0', glow: '#c0c0c040', badge: 'bg-gradient-to-br from-gray-300 to-gray-400' },
    3: { border: '#cd7f32', glow: '#cd7f3240', badge: 'bg-gradient-to-br from-amber-600 to-orange-700' },
  };
  const rankStyle = rankColors[rank] ?? {
    border: `${PATRON_COLOR}30`,
    glow: `${PATRON_COLOR}15`,
    badge: 'bg-gray-800',
  };

  const rankLabel = rank <= 3 ? ['👑', '🥈', '🥉'][rank - 1] : `#${rank}`;

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300 cursor-pointer group"
      style={{
        borderColor: rankStyle.border,
        background: `linear-gradient(135deg, ${PATRON_COLOR}08, transparent)`,
        boxShadow: `0 0 16px ${rankStyle.glow}`,
      }}
      onClick={() => credit.artistUserId && navigate(`/artist/u/${credit.artistUserId}`)}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 0 28px ${rankStyle.glow}`;
        e.currentTarget.style.borderColor = PATRON_COLOR;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = `0 0 16px ${rankStyle.glow}`;
        e.currentTarget.style.borderColor = rankStyle.border;
      }}
    >
      {/* 順位バッジ */}
      <div
        className={`flex-shrink-0 ${compact ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base'} flex items-center justify-center rounded-full font-bold ${rank <= 3 ? rankStyle.badge : ''}`}
        style={rank > 3 ? { background: `${PATRON_COLOR}15`, color: `${PATRON_COLOR}80` } : {}}
      >
        <span className={rank <= 3 ? 'text-black' : ''}>{rankLabel}</span>
      </div>

      {/* アバター */}
      <div
        className={`flex-shrink-0 ${compact ? 'w-9 h-9' : 'w-11 h-11'} rounded-full overflow-hidden border-2`}
        style={{ borderColor: `${PATRON_COLOR}50` }}
      >
        {credit.imageUrl ? (
          <img
            src={credit.imageUrl}
            alt={credit.name}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${PATRON_COLOR}20, ${PATRON_COLOR}08)` }}
          >
            <i className="ri-user-line text-sm" style={{ color: `${PATRON_COLOR}60` }} />
          </div>
        )}
      </div>

      {/* 名前・場所 */}
      <div className="flex-1 min-w-0">
        <p className={`text-white font-bold truncate ${compact ? 'text-sm' : 'text-base'}`}>
          {credit.name}
        </p>
        {credit.location && (
          <div className="flex items-center gap-1 mt-0.5">
            <i className="ri-map-pin-line text-[10px]" style={{ color: `${PATRON_COLOR}70` }} />
            <span className="text-[10px] tracking-wider" style={{ color: `${PATRON_COLOR}70` }}>
              {credit.location}
            </span>
          </div>
        )}
      </div>

      {/* 累計支援額 */}
      <div className="flex-shrink-0 text-right">
        <p
          className={`font-mono font-bold ${compact ? 'text-sm' : 'text-base'}`}
          style={{ color: PATRON_COLOR, textShadow: `0 0 10px ${PATRON_COLOR}60` }}
        >
          {(credit.totalAmount ?? 0).toLocaleString()}
          <span className="text-[10px] ml-0.5 opacity-70">pt</span>
        </p>
        <p className="text-[10px] text-gray-600 tracking-wider">TOTAL</p>
      </div>

      {/* 矢印 */}
      {credit.artistUserId && (
        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center opacity-40 group-hover:opacity-100 transition-opacity">
          <i className="ri-arrow-right-s-line text-sm" style={{ color: PATRON_COLOR }} />
        </div>
      )}
    </div>
  );
}

// ── PATRON ランキングセクション ────────────────────────────────────────
interface PatronRankingSectionProps {
  patrons: CreditEntry[];
  compact: boolean;
  navigate: ReturnType<typeof useNavigate>;
}

function PatronRankingSection({ patrons, compact, navigate }: PatronRankingSectionProps) {
  const [showAll, setShowAll] = React.useState(false);
  const INITIAL_SHOW = compact ? 5 : 8;
  const displayed = showAll ? patrons : patrons.slice(0, INITIAL_SHOW);
  const totalAmount = patrons.reduce((sum, p) => sum + (p.totalAmount ?? 0), 0);

  return (
    <div>
      {/* セクションヘッダー */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-full border-2"
          style={{
            borderColor: `${PATRON_COLOR}60`,
            background: `linear-gradient(135deg, ${PATRON_COLOR}15, ${PATRON_COLOR}05)`,
            boxShadow: `0 0 20px ${PATRON_COLOR}20`,
          }}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-vip-crown-line text-sm" style={{ color: PATRON_COLOR }} />
          </div>
          <span className="text-xs font-bold tracking-widest" style={{ color: PATRON_COLOR }}>
            PATRON
          </span>
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
            style={{ background: `${PATRON_COLOR}30`, color: PATRON_COLOR }}
          >
            {patrons.length}
          </span>
        </div>
        <div
          className="flex-1 h-px"
          style={{ background: `linear-gradient(to right, ${PATRON_COLOR}30, transparent)` }}
        />
        {/* 累計支援総額 */}
        <div className="flex items-center gap-1.5">
          <i className="ri-sparkling-2-line text-xs" style={{ color: `${PATRON_COLOR}70` }} />
          <span className="text-xs font-mono font-bold" style={{ color: `${PATRON_COLOR}90` }}>
            {totalAmount.toLocaleString()} pt
          </span>
        </div>
      </div>

      {/* 説明文 */}
      <p className="text-gray-600 text-[10px] leading-relaxed mb-4 tracking-wide">
        {COLLECTIVE_TYPES[0].description}
      </p>

      {/* ランキングリスト */}
      <div className="space-y-2">
        {displayed.map((patron, index) => (
          <PatronRow
            key={patron.artistUserId || index}
            credit={patron}
            rank={index + 1}
            compact={compact}
            navigate={navigate}
          />
        ))}
      </div>

      {/* もっと見る / 折りたたむ */}
      {patrons.length > INITIAL_SHOW && (
        <button
          className="mt-4 w-full py-2.5 rounded-xl border text-xs font-bold tracking-widest transition-all duration-300 hover:scale-[1.01] cursor-pointer whitespace-nowrap"
          style={{
            borderColor: `${PATRON_COLOR}30`,
            color: `${PATRON_COLOR}80`,
            background: `${PATRON_COLOR}08`,
          }}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? `▲ 折りたたむ`
            : `▼ さらに ${patrons.length - INITIAL_SHOW} 人を表示`}
        </button>
      )}
    </div>
  );
}

// ── 通常クレジットカード ───────────────────────────────────────────────
interface CreditCardProps {
  credit: CreditEntry;
  accentColor: string;
  compact: boolean;
  navigate: ReturnType<typeof useNavigate>;
  isPatron?: boolean;
}

function CreditCard({ credit, accentColor, compact, navigate, isPatron = false }: CreditCardProps) {
  const hasLink = Boolean(credit.artistUserId);
  const hasSnsLink = Boolean(credit.snsUrl);
  const isClickable = hasLink || hasSnsLink;
  const displayColor = isPatron ? PATRON_COLOR : accentColor;
  const cardWidth = compact ? 'w-64' : 'w-72';
  const imageHeight = compact ? 'h-28' : 'h-40';
  const nameFontSize = compact ? 'text-sm' : 'text-base';
  const genreFontSize = compact ? 'text-xs' : 'text-sm';
  const descFontSize = compact ? 'text-xs' : 'text-sm';
  const padding = compact ? 'p-4' : 'p-5';
  const iconSize = compact ? 'text-xs' : 'text-sm';
  const linkIconSize = compact ? 'w-7 h-7' : 'w-8 h-8';

  const handleCardClick = () => {
    if (hasLink) navigate(`/artist/u/${credit.artistUserId}`);
    else if (hasSnsLink) window.open(credit.snsUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`flex-shrink-0 ${cardWidth} bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border transition-all duration-500 group overflow-hidden ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
      style={{
        borderColor: isPatron ? `${displayColor}50` : `${displayColor}30`,
        boxShadow: isPatron ? `0 0 25px ${displayColor}20` : `0 0 20px ${displayColor}10`,
      }}
      onClick={isClickable ? handleCardClick : undefined}
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.borderColor = displayColor;
        e.currentTarget.style.boxShadow = isPatron ? `0 0 40px ${displayColor}40` : `0 0 30px ${displayColor}25`;
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.borderColor = isPatron ? `${displayColor}50` : `${displayColor}30`;
        e.currentTarget.style.boxShadow = isPatron ? `0 0 25px ${displayColor}20` : `0 0 20px ${displayColor}10`;
      }}
    >
      <div className={`relative w-full ${imageHeight} overflow-hidden bg-black/50`}>
        {credit.imageUrl ? (
          <img
            src={credit.imageUrl}
            alt={credit.name ?? 'Credit image'}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${displayColor}15, ${displayColor}05)` }}
          >
            <i className={`ri-user-line ${compact ? 'text-4xl' : 'text-5xl'}`} style={{ color: `${displayColor}40` }} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent" />
        {hasLink && (
          <div
            className={`absolute top-3 right-3 ${compact ? 'w-7 h-7' : 'w-8 h-8'} flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm border`}
            style={{ borderColor: `${displayColor}50` }}
          >
            <i className={`ri-link ${iconSize}`} style={{ color: displayColor }} />
          </div>
        )}
        {!hasLink && hasSnsLink && (
          <div
            className={`absolute top-3 right-3 ${compact ? 'w-7 h-7' : 'w-8 h-8'} flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm border`}
            style={{ borderColor: `${displayColor}50` }}
          >
            <i className={`ri-external-link-line ${iconSize}`} style={{ color: displayColor }} />
          </div>
        )}
      </div>
      <div className={padding}>
        <h4 className={`text-white font-bold ${nameFontSize} truncate mb-1`}>{credit.name ?? 'Unnamed'}</h4>
        <p className={`${genreFontSize} mb-3 truncate font-semibold`} style={{ color: `${displayColor}cc` }}>
          {credit.genre ?? 'Unknown'}
        </p>
        {credit.description && (
          <p className={`text-gray-500 ${descFontSize} leading-relaxed mb-3 line-clamp-2`}>{credit.description}</p>
        )}
        <div className={`flex items-center justify-between ${compact ? '' : 'pt-2 border-t border-white/5'}`}>
          {credit.location && (
            <div className="flex items-center gap-1.5">
              <i className="ri-map-pin-line text-[10px]" style={{ color: displayColor }} />
              <span className="text-[10px] tracking-wider" style={{ color: displayColor }}>{credit.location}</span>
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {hasSnsLink && (
              <div
                className={`${linkIconSize} flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:border-white/30 hover:bg-white/10 transition-all duration-300`}
                onClick={(e) => { e.stopPropagation(); window.open(credit.snsUrl, '_blank', 'noopener,noreferrer'); }}
              >
                <i className={`ri-global-line ${iconSize} text-gray-400 hover:text-white`} />
              </div>
            )}
            {hasLink && (
              <div className="flex items-center gap-1 text-[10px] tracking-wider opacity-60 group-hover:opacity-100 transition-opacity" style={{ color: displayColor }}>
                <span>{compact ? 'VIEW' : 'VIEW PROFILE'}</span>
                <i className="ri-arrow-right-s-line text-xs group-hover:translate-x-0.5 transition-transform" />
              </div>
            )}
            {!hasLink && hasSnsLink && (
              <div className="flex items-center gap-1 text-[10px] tracking-wider opacity-60 group-hover:opacity-100 transition-opacity" style={{ color: displayColor }}>
                <span>{compact ? 'LINK' : 'OPEN LINK'}</span>
                <i className="ri-arrow-right-s-line text-xs group-hover:translate-x-0.5 transition-transform" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── メインコンポーネント ───────────────────────────────────────────────
export function LayerCredits({
  accentColor,
  credits,
  hasCreditsLayer,
  compact = false,
}: LayerCreditsProps) {
  const navigate = useNavigate();

  if (!hasCreditsLayer) {
    return (
      <EmptyLayer
        accentColor={accentColor}
        layerLabel="Layer 03 / Collectives"
        icon="ri-group-line"
      />
    );
  }

  const patronCredits = credits.filter(c => c.genre === 'PATRON');
  const memberCredits = credits.filter(c => c.collectiveType === 'member' && c.genre !== 'PATRON');
  const supportCredits = credits.filter(c => c.collectiveType === 'support' && c.genre !== 'PATRON');
  const otherCredits = credits.filter(c => !c.collectiveType && c.genre !== 'PATRON');
  const hasGroups = patronCredits.length > 0 || memberCredits.length > 0 || supportCredits.length > 0;

  const renderScrollRow = (items: CreditEntry[], color: string) => (
    <div className="relative">
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {items.map((credit, index) => (
          <CreditCard key={index} credit={credit} accentColor={color} compact={compact} navigate={navigate} />
        ))}
      </div>
      {items.length > (compact ? 1 : 3) && (
        <div className="absolute right-0 top-0 bottom-4 w-16 bg-gradient-to-l from-black to-transparent pointer-events-none flex items-center justify-end pr-3">
          <i className="ri-arrow-right-s-line text-gray-600 text-2xl" />
        </div>
      )}
    </div>
  );

  return (
    <div className="mb-12">
      {/* ヘッダー */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-2 h-2 rounded-full" style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }} />
        <p className="text-gray-500 text-xs tracking-widest uppercase">Layer 03 / Collectives</p>
      </div>

      {/* グラスモーフィズムパネル */}
      <div
        className="rounded-2xl p-5 border backdrop-blur-md"
        style={{
          background: 'rgba(255,255,255,0.02)',
          borderColor: `${accentColor}18`,
          boxShadow: `0 0 40px ${accentColor}08`,
        }}
      >
        {hasGroups ? (
          <div className="space-y-8">
            {patronCredits.length > 0 && (
              <PatronRankingSection patrons={patronCredits} compact={compact} navigate={navigate} />
            )}

            {memberCredits.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
                    style={{ borderColor: `${COLLECTIVE_TYPES[1].color}40`, background: `${COLLECTIVE_TYPES[1].color}10` }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${COLLECTIVE_TYPES[1].icon} text-sm`} style={{ color: COLLECTIVE_TYPES[1].color }} />
                    </div>
                    <span className="text-xs font-bold tracking-widest" style={{ color: COLLECTIVE_TYPES[1].color }}>MEMBER</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: `${COLLECTIVE_TYPES[1].color}20`, color: COLLECTIVE_TYPES[1].color }}>
                      {memberCredits.length}
                    </span>
                  </div>
                  <div className="flex-1 h-px" style={{ background: `${COLLECTIVE_TYPES[1].color}15` }} />
                </div>
                <p className="text-gray-600 text-[10px] leading-relaxed mb-4 tracking-wide">{COLLECTIVE_TYPES[1].description}</p>
                {renderScrollRow(memberCredits, COLLECTIVE_TYPES[1].color)}
              </div>
            )}

            {supportCredits.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
                    style={{ borderColor: `${COLLECTIVE_TYPES[2].color}40`, background: `${COLLECTIVE_TYPES[2].color}10` }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${COLLECTIVE_TYPES[2].icon} text-sm`} style={{ color: COLLECTIVE_TYPES[2].color }} />
                    </div>
                    <span className="text-xs font-bold tracking-widest" style={{ color: COLLECTIVE_TYPES[2].color }}>SUPPORT</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: `${COLLECTIVE_TYPES[2].color}20`, color: COLLECTIVE_TYPES[2].color }}>
                      {supportCredits.length}
                    </span>
                  </div>
                  <div className="flex-1 h-px" style={{ background: `${COLLECTIVE_TYPES[2].color}15` }} />
                </div>
                <p className="text-gray-600 text-[10px] leading-relaxed mb-4 tracking-wide">{COLLECTIVE_TYPES[2].description}</p>
                {renderScrollRow(supportCredits, COLLECTIVE_TYPES[2].color)}
              </div>
            )}

            {otherCredits.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
                    style={{ borderColor: `${accentColor}30`, background: `${accentColor}08` }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-group-line text-sm" style={{ color: accentColor }} />
                    </div>
                    <span className="text-xs font-bold tracking-widest" style={{ color: accentColor }}>OTHERS</span>
                  </div>
                  <div className="flex-1 h-px" style={{ background: `${accentColor}15` }} />
                </div>
                {renderScrollRow(otherCredits, accentColor)}
              </div>
            )}
          </div>
        ) : (
          <div className="relative">
            <div className="flex gap-4 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {credits.map((credit, index) => (
                <CreditCard key={index} credit={credit} accentColor={accentColor} compact={compact} navigate={navigate} />
              ))}
            </div>
            {credits.length > (compact ? 1 : 3) && (
              <div className="absolute right-0 top-0 bottom-4 w-16 bg-gradient-to-l from-black to-transparent pointer-events-none flex items-center justify-end pr-3">
                <i className="ri-arrow-right-s-line text-gray-600 text-2xl" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
